import "../style.scss"
import "./styles.scss"

import { CircularProgress } from "@mui/material"
import Backdrop from "@mui/material/Backdrop"
import _get from "lodash/get"
import moment from "moment"
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react"

import AccessControl from "../../../components/access/AccessControl"
import PasswordProtected from "../../../components/access/PasswordProtected"
import Error from "../../../components/error"
import Head from "../../../components/head"
import { X_TF_ECOMMERCE } from "../../../components/header"
import Layout from "../../../components/layout"
import { ENV } from "../../../constants/env"
import { getEvent } from "../../../http_service"
import { deleteCookieByName, getCookieByName } from "../../../utils/cookies"
import { defaultThemeSettings, setTfCheckoutReactConfigs } from "../../../utils/tf-checkout-config"
import NotFoundPage from "../../404"
import BillingPage from "../../billing/billing-info"

const TicketsContainer = lazy(() => import("tf-checkout-react").then(module => ({ default: module.TicketsContainer })))

const cookie = require("cookie")

const isWindowDefined = typeof window !== "undefined"

setTfCheckoutReactConfigs()


const EventPage = ({ serverData }) => {
  const [eventData, setEventData] = useState({
    event: {},
    venue: {},
    slug: "",
    pk: "",
    tickets: []
  })

  const [uiState, setUiState] = useState({
    loading: false,
    showBillingInfo: false,
    isLoggedIn: false,
    isHydrated: false,
    queryPromoCode: ""
  })

  const [checkoutData, setCheckoutData] = useState({})

  const eventSlug = serverData?.slug
  const eventId = serverData?.eventId
  const isPasswordProtected = serverData?.error && serverData?.message?.status === 401

  useEffect(() => {
      setUiState(prev => ({
        ...prev,
        isHydrated: true,
        isLoggedIn: !!getCookieByName(X_TF_ECOMMERCE),
        queryPromoCode: new URL(window.location.href).searchParams.get("r") || ""
      }))
  }, [])

  useEffect(() => {
    if (serverData) {
      setEventData({
        event: _get(serverData, "event.event", {}),
        venue: _get(serverData, "event.venue", {}),
        slug: _get(serverData, "slug", ""),
        pk: _get(serverData, "pk", "")
      })
    }
  }, [serverData])

  const handleCartSuccessRedirect = useCallback((data, event_id = "") => {
    if (!isWindowDefined) {
      return
    }
    setCheckoutData(data)

    if(data.skip_billing_page) {
      window.location.href = `/billing/checkout?event_id=${event_id}`
    } else {
      setUiState(prev => ({ ...prev, showBillingInfo: true }))
    }
  }, [])
  

  if (serverData.showErrorPage) {
    return <NotFoundPage />
  }

  if (serverData.storedPassword !== ENV.TTF_ACCESS && serverData.host?.includes("tickets-staging.manacommon.com")) {
    return <AccessControl />
  }

  const { name, start_date, end_date, image_full_url, description, id, prereg_enabled, prereg_ended } =
    isPasswordProtected ? eventData.event : _get(serverData, "event.event", {})

  const date = moment(start_date).format("dd.DD.MM.YY")
  const startHour = moment(start_date).format("h:mm a")
  const endHour = moment(end_date).format("h:mm a")

  const getAuthorizedEvent = async () => {
    setUiState(prev => ({ ...prev, loading: true }))
    try {
      const response = await getEvent(eventData.slug, eventData.pk)
      setEventData({
        event: _get(response, "event"),
        venue: _get(response, "venue", {}),
        slug: _get(response, "slug", ""),
        pk: _get(response, "pk", "")
      })
    } catch (e) {
    } finally {
      setUiState(prev => ({ ...prev, loading: false }))
    }
  }

  if (uiState.loading) {
    return (
      <Backdrop sx={{ color: "#fff", backgroundColor: "transparent", zIndex: 1205 }} open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    )
  }

  if (serverData.error && Object.keys(eventData.event).length === 0) {
    if (serverData.message.status === 401) {
      return (
        <Layout>
          <PasswordProtected
            slug={eventData.slug || eventSlug}
            pk={eventData.pk}
            id={id || eventId}
            getAuthorizedEvent={getAuthorizedEvent}
          />
        </Layout>
      )
    }
    return <Error />
  }

  const handleReserveButtonClick = () => {
    if (isWindowDefined) {
      window.location.href = `/events/${slug || eventSlug}/seat-map?event_id=${id || eventId}`
    }
  }

  const cartItems = Array.isArray(checkoutData.cart) ? checkoutData.cart : []
  const totalTicketsSelected = cartItems.reduce((total, item) => total + Number(item.quantity || 0), 0)
  const totalTicketsAmount = cartItems.reduce((total, item) => total + Number(item.price || 0), 0)
  const formattedTotalTicketsAmount = totalTicketsAmount.toFixed(2)
  // Temp redirection
  if (_get(serverData, "event.event.skip_details_page", false)) {
    handleReserveButtonClick()
    return null
  }

  return (
    <>
      <Head title="Event Details Page" />
      <Layout>
        <div className="event-details-container" itemScope itemType="https://schema.org/Event">
          <div className="event-detail-row-item top-radius">
            <div className="title">
              <h2 className="event-name">{name}</h2>
              <p className="event-info">{date}</p>
              <p className="event-name">
                {startHour} - {endHour}
              </p>
              <p className="event-name">{eventData.venue.name}</p>
            </div>
          </div>
          <div className="event-detail-row-item white no-radius">
            <div
              className="description"
              dangerouslySetInnerHTML={{
                __html: description
              }}
            />
          </div>
          <div
            className="event-detail-row-item white"
            style={{ maxWidth: "100%", boxSizing: "border-box", paddingBlock: "0" }}
          >
            {uiState.isHydrated && image_full_url && (
              <img
                src={image_full_url}
                alt="Event flyer artwork"
                style={{
                  width: "100%",
                  objectFit: "contain",
                  border: "1px solid #eee",
                  borderRadius: "4px"
                }}
              />
            )}
          </div>
          <div className="event-detail-row-item white bottom-radius">
            {prereg_enabled && !prereg_ended ? null : (
              <Suspense fallback={<CircularProgress color="inherit" />}>
                <>
                <div style={{ width: "100%", visibility: uiState.showBillingInfo ? "hidden" : "visible", height: uiState.showBillingInfo ? "0" : "auto" }}>
                <TicketsContainer
                  theme="light"
                  themeOptions={defaultThemeSettings}
                  eventId={id}
                  handleNotInvitedModalClose={() => {}}
                  handleInvalidLinkModalClose={() => {}}
                  onAddToCartSuccess={(data) => {
                    handleCartSuccessRedirect(data, id)
                  }}
                  isPromotionsEnabled={eventData.event?.is_promotions_enabled}
                  isAccessCodeEnabled={eventData.event?.is_access_code}
                  onLogoutSuccess={() => {
                    if (isWindowDefined) {
                      window.location.href = "/"
                    }
                  }}
                  queryPromoCode={uiState.queryPromoCode}
                  hideSessionButtons={true}
                  //onGetTicketsSuccess={() => {}}
                  enableAddOns={true}
                  showGroupNameBlock={true}
                  onReserveButtonClick={handleReserveButtonClick}
                  tableTicketsHeaderComponent={<div className="tickets-container-header">RESERVE TABLES</div>}
                  onPendingVerification={() => {
                    if (isWindowDefined) {
                      window.location.replace(`/verification`)
                    }
                  }}
                  onGetProfileDataError={error => {
                    if (error.response.status === 401) {
                      deleteCookieByName(X_TF_ECOMMERCE)
                      if (isWindowDefined) {
                        window.localStorage.removeItem("user_data")
                      }
                    }
                  }}
                />
                </div>
                  {uiState.showBillingInfo && (
                    <div className="billing-controls-section">
                      <div className="selected-tickets-info">
                        {totalTicketsSelected > 0 && (
                          <span className="selection-summary">
                            {totalTicketsSelected} ticket{totalTicketsSelected > 1 ? 's' : ''} selected • {checkoutData.currencySymbol}{formattedTotalTicketsAmount}
                          </span>
                        )}
                      </div>
                      <button
                        className="edit-tickets-btn"
                        onClick={() => setUiState(prev => ({ ...prev, showBillingInfo: false }))}
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </>
                {uiState.showBillingInfo && (
                    <BillingPage />
                )}
              </Suspense>
            )}
          </div>
        </div>
      </Layout>
    </>
  )
}

export async function getServerData(props) {
  const cookies = props.headers.get("cookie")
  let storedPassword = ""
  if (cookies) {
    storedPassword = cookie.parse(cookies)?.["ttf-access"] || ""
  }
  const {
    params: { slug },
    query: { pk, eventId }
  } = props

  try {
    const event = await getEvent(slug, pk)
    const { end_date } = event.event

    const endDateUnindex = moment(end_date).add(2, "days")
    const isAfterEndDate = moment().isAfter(endDateUnindex)

    if (isAfterEndDate) {
      return {
        status: 404,
        props: {
          showErrorPage: true
        }
      }
    }

    return {
      props: {
        event,
        slug,
        pk,
        eventId,
        storedPassword,
        host: props.headers.get("host")
      }
    }
  } catch (error) {
    return {
      props: {
        error: true,
        message: error,
        slug,
        pk,
        eventId: error?.response?.data?.event_id
      }
    }
  }
}

export default EventPage
